import { columnDestinationHelpContent, HelpTooltip } from '@front/components/HelpTooltip';
import { usePermissions } from '@front/hook/usePermissions';
import {
  Button,
  List,
  ListBody,
  ListCell,
  ListHead,
  ListHeadCell,
  ListRow,
  Loader,
  ScrollArea,
  Textarea,
  TextField,
  Tooltip,
} from '@plaidev/sour-react';
import { createFileRoute, type ErrorComponentProps } from '@tanstack/react-router';
import { Controller } from 'react-hook-form';
import { DomainV2 } from 'troppus-common/domain';
import { importTableDefinitionColumnTypeLabel } from './importTableDefinitionColumnTypeLabel';
import { importTableDefinitionLoadModeLabel } from './importTableDefinitionLoadModeLabel';
import { LatestImportSummarySection } from './LatestImportSummarySection';
import { RevisionHistorySection } from './RevisionHistorySection';
import { hasRuleKind } from './transformRules';
import { useTableDefinitionDetail } from './useTableDefinitionDetail';

import ImportTableDefinition = DomainV2.Import.ImportTableDefinition;

export const Route = createFileRoute('/table-definitions/$id')({
  component: TableDefinitionDetailPage,
  errorComponent: TableDefinitionDetailError,
});

function TableDefinitionDetailError({ error }: ErrorComponentProps) {
  const message = error instanceof Error ? error.message : 'データの取得に失敗しました';
  return <div className="rounded-lg border border-negative bg-negative-light p-4 text-sm text-negative">{message}</div>;
}

function destinationJa(d: ImportTableDefinition.SchemaJson.Column['destination']): string {
  if (d === 'customer') return '顧客';
  if (d === 'contract') return '契約';
  return '無視';
}

function destinationBadgeClass(d: ImportTableDefinition.SchemaJson.Column['destination']): string {
  if (d === 'customer') return 'bg-primary-light text-primary';
  if (d === 'contract') return 'bg-notice-light text-notice';
  return 'bg-subtle text-muted-light';
}

function checkMark(isIgnore: boolean, value: boolean): string {
  if (isIgnore) return '—';
  return value ? '✓' : '';
}

/**
 * @feature テーブル定義詳細
 * @capabilities 表示名・説明の編集, CSVアップロード画面への遷移, 最新取込サマリ表示, リビジョン履歴表示, CSV設定・一意キー・列定義の確認
 * @behavior 表示名・説明を編集→「保存」で更新（プロジェクト管理者のみ）, 「CSVアップロードへ」→取込画面へ遷移（未完了タスクがある場合は無効化しツールチップで通知）, 列定義テーブルで各列の振り分け先（顧客/契約/無視）・型・必須・カナ/電話正規化・一意キーを確認
 * @airule IMPORTANT: Update this JSDoc when modifying this component's functionality
 */
function TableDefinitionDetailPage() {
  const {
    data,
    isPending,
    control,
    isDirty,
    isValid,
    updateMutationIsPending,
    onSubmit,
    navigateBack,
    navigateToUpload,
    revisions,
    revisionsPending,
    creationTaskByRevisionId,
    navigateToTask,
    navigateToTaskList,
    incompleteImportTasks,
    hasIncompleteImportTask,
    latestFinishedImportTask,
    importTasksPending,
  } = useTableDefinitionDetail();
  const { isProjectAdmin } = usePermissions();

  if (isPending) {
    return (
      <div className="flex justify-center py-16">
        <Loader />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col gap-4">
        <p className="text-sm text-muted-light">テーブル定義が見つかりません。</p>
        <Button variant="quiet" size="sm" onClick={navigateBack}>
          一覧へ戻る
        </Button>
      </div>
    );
  }

  const { definition, lastRevision } = data;
  const { schemaJson, uniqueKeys, loadMode } = lastRevision;

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-8">
      <form className="flex flex-col gap-6" onSubmit={(e) => void onSubmit(e)}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h1 className="text-xl font-semibold text-default">{definition.displayName}</h1>
          <div className="flex items-center gap-2">
            <Button variant="basic" size="sm" onClick={navigateBack}>
              戻る
            </Button>
            {isProjectAdmin && (
              <Button type="submit" variant="primary" size="sm" loading={updateMutationIsPending} disabled={!isDirty || !isValid}>
                保存
              </Button>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-default">
            表示名<span className="text-negative">*</span>
          </span>
          <Controller
            control={control}
            name="displayName"
            rules={{ validate: (v) => v.trim().length > 0 || '表示名を入力してください' }}
            render={({ field }) => (
              <TextField variant="contained" value={field.value} onChange={field.onChange} required disabled={!isProjectAdmin} />
            )}
          />
        </div>
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-default">説明</span>
          <Controller
            control={control}
            name="description"
            render={({ field }) => (
              <Textarea
                className="[&>textarea]:w-full"
                value={field.value}
                onChange={field.onChange}
                rows={4}
                placeholder="説明を入力"
                disabled={!isProjectAdmin}
              />
            )}
          />
        </div>
      </form>

      {isProjectAdmin && (
        <section className="flex flex-col gap-3">
          <h2 className="text-sm font-semibold text-default">CSV を取り込む</h2>
          <div className="flex flex-col gap-3 rounded-lg border border-default p-4">
            <p className="text-sm text-muted-light">
              CSV アップロードページに移動して、このテーブル定義の取り込みタスクを起動します。
              {loadMode === 'Replace'
                ? 'この定義で過去に取り込んだデータを洗い替えます。'
                : '同じキーのデータは更新、無いデータは新規作成されます。'}
            </p>
            <div className="flex flex-wrap gap-3">
              {hasIncompleteImportTask ? (
                <Tooltip
                  content={
                    <p className="max-w-xs text-left text-xs">
                      未完了の取り込みタスクがあります。完了してから新しい取り込みを開始できます（現在:{' '}
                      {incompleteImportTasks[0]?.status === 'Requested' ? '受付済み' : '実行中'}）。
                    </p>
                  }
                >
                  <span className="inline-flex">
                    <Button type="button" variant="primary" size="sm" disabled>
                      CSVアップロードへ
                    </Button>
                  </span>
                </Tooltip>
              ) : (
                <Button type="button" variant="primary" size="sm" onClick={navigateToUpload}>
                  CSVアップロードへ
                </Button>
              )}
            </div>
          </div>
        </section>
      )}

      <LatestImportSummarySection
        latestFinishedImportTask={latestFinishedImportTask}
        incompleteImportTask={incompleteImportTasks[0]}
        isPending={importTasksPending}
        onNavigateToTask={navigateToTask}
      />

      <RevisionHistorySection
        revisions={revisions}
        isPending={revisionsPending}
        activeRevisionId={data.definition.activeRevisionId}
        lastRevisionId={data.definition.lastRevisionId}
        creationTaskByRevisionId={creationTaskByRevisionId}
        onNavigateToTaskList={navigateToTaskList}
      />

      <section className="flex flex-col gap-3">
        <h2 className="text-sm font-semibold text-default">CSV 設定</h2>
        <div className="rounded-lg border border-default p-4">
          <dl className="grid gap-3 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-muted-light">文字コード</dt>
              <dd className="text-default">{schemaJson.encoding}</dd>
            </div>
            <div>
              <dt className="text-muted-light">区切り文字</dt>
              <dd className="text-default">{schemaJson.delimiter === '\t' ? 'TAB' : schemaJson.delimiter}</dd>
            </div>
            <div>
              <dt className="text-muted-light">ヘッダー行</dt>
              <dd className="text-default">{schemaJson.hasHeaderRow ? 'あり' : 'なし'}</dd>
            </div>
            <div>
              <dt className="text-muted-light">取込モード</dt>
              <dd className="text-default">{importTableDefinitionLoadModeLabel(loadMode)}</dd>
            </div>
          </dl>
        </div>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-sm font-semibold text-default">一意キー</h2>
        <div className="flex flex-col gap-3 rounded-lg border border-default p-4">
          <div className="text-sm">
            <p className="text-muted-light">契約</p>
            <p className="text-default">{uniqueKeys.contract.columnNames.join('、') || '—'}</p>
          </div>
          {uniqueKeys.customer ? (
            <div className="text-sm">
              <p className="text-muted-light">顧客</p>
              <p className="text-default">
                {uniqueKeys.customer.columnNames.join('、')}（識別子: {uniqueKeys.customer.identifierType}）
              </p>
            </div>
          ) : (
            <p className="text-sm text-muted-light">顧客の一意キーは未設定です。</p>
          )}
        </div>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-sm font-semibold text-default">列定義</h2>
        <div className="max-h-[540px] overflow-y-auto rounded-lg border border-default">
          <ScrollArea className="w-full min-w-0 max-w-full" orientation="horizontal">
            <div className="w-full min-w-[952px]">
              <List stickyTop={false}>
                <ListHead>
                  <ListRow>
                    <ListHeadCell>列名</ListHeadCell>
                    <ListHeadCell width={180} inextensible>
                      <span className="inline-flex items-center gap-1">
                        振り分け先
                        <HelpTooltip
                          side="top"
                          ariaLabel="振り分け先の説明"
                          iconClassName="size-3.5 shrink-0 text-muted-light"
                          content={columnDestinationHelpContent}
                        />
                      </span>
                    </ListHeadCell>
                    <ListHeadCell width={90} inextensible>
                      型
                    </ListHeadCell>
                    <ListHeadCell width={64} align="center" inextensible>
                      必須
                    </ListHeadCell>
                    <ListHeadCell width={80} align="center" inextensible>
                      カナ正規化
                    </ListHeadCell>
                    <ListHeadCell width={96} align="center" inextensible>
                      電話正規化
                    </ListHeadCell>
                    <ListHeadCell width={72} align="center" inextensible>
                      複数値
                    </ListHeadCell>
                    <ListHeadCell width={72} align="center" inextensible>
                      契約UK
                    </ListHeadCell>
                    <ListHeadCell width={72} align="center" inextensible>
                      顧客UK
                    </ListHeadCell>
                  </ListRow>
                </ListHead>
                <ListBody>
                  {schemaJson.columns.map((col) => {
                    const isIgnore = col.destination === 'ignore';
                    const isContractUk = uniqueKeys.contract.columnNames.includes(col.name);
                    const isCustomerUk = uniqueKeys.customer?.columnNames.includes(col.name) ?? false;
                    return (
                      <ListRow key={col.name} className={isIgnore ? 'opacity-60' : undefined}>
                        <ListCell>
                          <span className="text-sm font-medium text-default">{col.name}</span>
                        </ListCell>
                        <ListCell>
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs ${destinationBadgeClass(col.destination)}`}
                          >
                            {destinationJa(col.destination)}
                          </span>
                        </ListCell>
                        <ListCell>
                          <span className="text-sm">{isIgnore ? '—' : importTableDefinitionColumnTypeLabel(col.type)}</span>
                        </ListCell>
                        <ListCell align="center">
                          <span className="text-sm">{checkMark(isIgnore, col.required)}</span>
                        </ListCell>
                        <ListCell align="center">
                          <span className="text-sm">{checkMark(isIgnore, hasRuleKind(col.transformRules, 'normalizeKana'))}</span>
                        </ListCell>
                        <ListCell align="center">
                          <span className="text-sm">{checkMark(isIgnore, hasRuleKind(col.transformRules, 'normalizePhoneNumber'))}</span>
                        </ListCell>
                        <ListCell align="center">
                          <span className="text-sm">{checkMark(isIgnore || col.destination !== 'customer', col.multiValued === true)}</span>
                        </ListCell>
                        <ListCell align="center">
                          <span className="text-sm">{isContractUk ? '✓' : ''}</span>
                        </ListCell>
                        <ListCell align="center">
                          <span className="text-sm">{isCustomerUk ? '✓' : ''}</span>
                        </ListCell>
                      </ListRow>
                    );
                  })}
                </ListBody>
              </List>
            </div>
          </ScrollArea>
        </div>
      </section>
    </div>
  );
}
